import { Fragment } from 'react';
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { Icon } from '../Icon';
import { CMSLink } from '../CMSLink';
import { GeneralInformation } from '@/payload-types';
import type { IconSvg } from '@/types/IconSvg';

type navItemsFromGeneralInformation = NonNullable<
  GeneralInformation['navigationItems']
>;
type navItemFromNavItem = navItemsFromGeneralInformation[number];

type CMSLinkFromGeneralInformation = NonNullable<
  NonNullable<GeneralInformation['socialNetworks']>[number]['social']
>[number]['link'];

export default function FlyMenu({
  menuValues,
  callToAction,
  ctaIcon,
  icons,
}: {
  menuValues: navItemFromNavItem;
  callToAction: CMSLinkFromGeneralInformation;
  ctaIcon: string;
  /**
   * Icones deja resolues cote serveur, indexees par nom. FlyMenu est un
   * composant client : resoudre le nom ici obligerait a embarquer tout le jeu
   * FontAwesome dans le bundle navigateur.
   */
  icons: Record<string, IconSvg>;
}) {
  const { link, links, reactIconsIcon, id } = menuValues;

  return (
    menuValues && (
      <Popover className="relative">
        {({ close }) => (
          <div
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node)) {
                close();
              }
            }}
          >
            <PopoverButton className="inline-flex items-center rounded-lg px-2 py-1 text-sm text-slate-700 hover:bg-slate-100 hover:text-slate-900">
              <span>{link.label}</span>
              <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
            </PopoverButton>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <PopoverPanel
                className={`absolute left-1/2 z-10 mt-5 flex w-screen max-w-max -translate-x-1/2 px-4`}
              >
                <div
                  onClick={() => close()}
                  className="w-screen max-w-md flex-auto overflow-hidden rounded-3xl bg-white text-sm leading-6 shadow-lg ring-1 ring-gray-900/5 lg:max-w-3xl"
                >
                  <CMSLink
                    {...link}
                    className="flex items-center gap-x-2.5 bg-gray-50  p-3 px-6 text-sm font-semibold leading-6 text-gray-900 hover:bg-gray-100 sm:justify-center sm:px-0"
                  >
                    <Icon
                      icon={reactIconsIcon ? icons[reactIconsIcon] : null}
                      className="mr-2 h-4 w-4 flex-none text-gray-400"
                    />
                  </CMSLink>

                  <div className="grid grid-cols-1 gap-x-6 gap-y-1 p-4 lg:grid-cols-2">
                    {links &&
                      links.map(
                        ({
                          link: childLink,
                          id,
                          reactIconsIcon,
                          description,
                        }) => (
                          <div
                            key={id}
                            className="group relative flex gap-x-6 rounded-lg p-4 hover:bg-gray-50"
                          >
                            <div className="mt-1 flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
                              <Icon
                                icon={reactIconsIcon ? icons[reactIconsIcon] : null}
                                className="h-6 w-6 text-gray-600 group-hover:text-[#009EE2]"
                              />
                            </div>
                            <div>
                              <CMSLink
                                {...childLink}
                                parentUrl={link}
                                className="font-semibold text-gray-900"
                              >
                                <p className="mt-1 text-gray-600">
                                  {description}
                                </p>
                              </CMSLink>
                            </div>
                          </div>
                        ),
                      )}
                  </div>
                  <div className="bg-gray-50">
                    <CMSLink
                      {...callToAction}
                      className="flex items-center gap-x-2.5 p-3 px-6 text-sm font-semibold leading-6 text-gray-900 hover:bg-gray-100 sm:justify-center sm:px-0"
                    >
                      <Icon
                        icon={ctaIcon ? icons[ctaIcon] : null}
                        className="h-5 w-5 flex-none text-gray-400"
                      />
                    </CMSLink>
                  </div>
                </div>
              </PopoverPanel>
            </Transition>
          </div>
        )}
      </Popover>
    )
  );
}
